<template>
  <div class="gate-login" :class="`gate-login--${props.context}`">
    <button v-if="props.context === 'gate'" type="button" class="gate-login__back" @click="emit('back')">
      ← 사업구분 선택
    </button>
    <h2 id="gate-login-title" class="gate-login__title">
      {{ props.context === 'mall' ? '함우리몰 로그인' : '로그인' }}
    </h2>

    <PublicLoginKindChoice
      v-if="needsKindChoice"
      :kinds="pendingKinds || []"
      :busy="isSubmitting"
      @pick="onPickKind"
      @reset="onResetKind"
    />

    <form v-else class="gate-login__form" @submit.prevent="onSubmit">
      <KakaoLoginButton :busy="kakaoBusy" @click="onKakaoLogin" />
      <p class="gate-login__split">또는 아이디로 로그인</p>

      <label class="gate-login__field">
        <span>아이디</span>
        <input
          ref="userIdRef"
          v-model="form.userId"
          type="text"
          autocomplete="username"
          placeholder="아이디를 입력해 주세요"
          maxlength="40"
          :class="userIdInputState"
          @input="onUserIdInput"
        />
      </label>

      <label class="gate-login__field">
        <span>비밀번호</span>
        <input
          v-model="form.password"
          type="password"
          autocomplete="current-password"
          placeholder="비밀번호를 입력해 주세요"
          :class="passwordInputState"
        />
      </label>

      <label class="gate-login__auto">
        <input v-model="rememberMe" type="checkbox" />
        <span>자동 로그인</span>
      </label>

      <button type="submit" class="gate-login__submit" :disabled="isSubmitting">
        {{ isSubmitting ? '로그인 중...' : '로그인' }}
      </button>
    </form>

    <div v-if="!needsKindChoice" class="gate-login__links">
      <router-link to="/find-id">아이디 찾기</router-link>
      <span aria-hidden="true" />
      <router-link to="/find-password">비밀번호 찾기</router-link>
      <span aria-hidden="true" />
      <router-link :to="props.context === 'mall' ? '/mall/signup' : '/signup'">회원가입</router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import KakaoLoginButton from '@/components/auth/KakaoLoginButton.vue'
import PublicLoginKindChoice from '@/components/auth/PublicLoginKindChoice.vue'
import { startKakaoLogin } from '@/composables/useKakaoOAuth'
import { memberHomeAfterLogin } from '@/composables/useLoginDestination'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { useSellerAuth } from '@/composables/useSellerAuth'
import {
  decidePublicLoginKind,
  identifyPublicLogin,
  type PublicLoginKind,
} from '@/composables/useUnifiedLogin'
import { loadAutoLogin } from '@/constants/autoLogin'
import {
  inputValidationState,
  isLoginPasswordValid,
  isLoginUserIdValid,
  sanitizeLoginUserIdInput,
} from '@/utils/formValidation'

const props = withDefaults(
  defineProps<{
    context?: 'gate' | 'mall'
    redirectTo?: string
  }>(),
  {
    context: 'gate',
    redirectTo: '',
  },
)

const emit = defineEmits<{
  back: []
  success: []
}>()

const { login: loginMember } = useMemberAuth()
const { login: loginSeller } = useSellerAuth()
const { login: loginAdmin } = useAdminAuth()
const userIdRef = ref<HTMLInputElement | null>(null)
const form = reactive({
  userId: '',
  password: '',
})
const rememberMe = ref(loadAutoLogin('member') || loadAutoLogin('seller') || loadAutoLogin('admin'))
const isSubmitting = ref(false)
const kakaoBusy = ref(false)
const pendingKinds = ref<PublicLoginKind[] | null>(null)

const needsKindChoice = computed(() => Boolean(pendingKinds.value))

const userIdInputState = computed(() =>
  inputValidationState(form.userId, isLoginUserIdValid(form.userId)),
)
const passwordInputState = computed(() =>
  inputValidationState(form.password, isLoginPasswordValid(form.password)),
)

function onUserIdInput(event: Event) {
  form.userId = sanitizeLoginUserIdInput((event.target as HTMLInputElement).value)
  pendingKinds.value = null
}

function onResetKind() {
  pendingKinds.value = null
}

async function onKakaoLogin() {
  if (kakaoBusy.value || isSubmitting.value) return
  kakaoBusy.value = true
  try {
    await startKakaoLogin({
      remember: rememberMe.value,
      redirect: props.redirectTo || memberHomeAfterLogin(),
    })
  } catch (error) {
    kakaoBusy.value = false
    window.alert(error instanceof Error ? error.message : '카카오 로그인을 시작하지 못했습니다.')
  }
}

async function enterAs(kind: PublicLoginKind) {
  if (kind === 'seller') {
    await loginSeller(form.userId, form.password, rememberMe.value)
  } else if (kind === 'admin') {
    await loginAdmin(form.userId, form.password, rememberMe.value)
  } else {
    await loginMember(form.userId, form.password, rememberMe.value)
  }
  emit('success')
}

async function onPickKind(kind: PublicLoginKind) {
  if (isSubmitting.value) return
  isSubmitting.value = true
  try {
    await enterAs(kind)
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '로그인에 실패했습니다.')
  } finally {
    isSubmitting.value = false
  }
}

async function onSubmit() {
  if (isSubmitting.value) return
  if (!isLoginUserIdValid(form.userId)) {
    window.alert('아이디 형식을 확인해 주세요.')
    return
  }
  if (!isLoginPasswordValid(form.password)) {
    window.alert('비밀번호를 입력해 주세요.')
    return
  }

  isSubmitting.value = true
  try {
    const kinds = await identifyPublicLogin(form.userId, form.password)
    const decision = decidePublicLoginKind(kinds, false)
    if (decision === 'choose') {
      pendingKinds.value = kinds
      return
    }
    await enterAs(decision)
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '로그인에 실패했습니다.')
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  void nextTick(() => userIdRef.value?.focus())
})
</script>

<style lang="scss" src="./GateLoginPanel.scss"></style>
